import React from 'react';
import { Point } from 'highcharts';
import ReactDOMServer from 'react-dom/server';
import {
    LineAverageForStudy,
    LineAverageForStudySpecific,
    LineEventForStudy,
    LineSiteForStudy,
    LineSiteForStudyCumulative,
    LineSubjectForSubject,
    SparkLineExample,
    SubjectByVisitData,
} from '../dummy-page.data';
import { getLineSubtitleComponents, getSubtitle as getLineSubtitle } from '@components-clean/core/charts/line/utils';
import { FormatNumberForChart } from '@components-clean/core/charts/shared/number-utils';
import styles from '@components-clean/core/charts/shared/style.module.scss';
import { RAGLabel } from '@components-clean/core/charts/shared/utils';
import PanelFrame from '@components-clean/core/panel-frame';
import { LineSeriesDataProperties } from '@interfaces-clean/panels.interface';
import { PanelAverageLineThresholdType, PanelSeriesType, PanelTheme, PanelType } from 'types/graphql-global-types';

type LineChartsPageProps = {
    theme?: PanelTheme;
};

export const LineChartsPage = ({ theme }: LineChartsPageProps) => {
    return (
        <>
            <h2>Line</h2>
            <div className="charts-section k-d-grid k-gap-2">
                <PanelFrame
                    title="Subjects By Visit - Systolic Reading"
                    data-testid="line-subject-by-visit"
                    infoText={getLineSubtitleComponents(
                        {
                            type: PanelSeriesType.SUBJECT_BY_VISIT,
                        },
                        SubjectByVisitData.threshold1,
                        SubjectByVisitData.threshold2,
                        PanelAverageLineThresholdType.STUDY_SPECIFIC,
                    )}
                    subTitle={getLineSubtitle(
                        {
                            type: PanelSeriesType.SUBJECT_BY_VISIT,
                        },
                        SubjectByVisitData.threshold1,
                        SubjectByVisitData.threshold2,
                        PanelAverageLineThresholdType.STUDY_SPECIFIC,
                    )}
                    panelType={PanelType.GRAPH}
                    seriesConfig={{ type: PanelSeriesType.SUBJECT_BY_VISIT }}
                    graphType="SCATTERPLOTLINE"
                    xAxisTitle="Visits"
                    yAxisTitle="Systolic Reading"
                    panelData={{
                        threshold1: SubjectByVisitData.threshold1,
                        threshold2: SubjectByVisitData.threshold2,
                        lineSubjectByVisitForStudy: SubjectByVisitData.lineSubjectByVisitForStudy,
                        lineSubjectByVisitCategories: SubjectByVisitData.lineSubjectByVisitCategories,
                    }}
                    showLegend
                    disablePanelSetup
                    theme={theme}
                />

                <PanelFrame
                    title="Site Standard - Enrollement and Issues - Months"
                    data-testid="line-series-site-standard"
                    panelType={PanelType.GRAPH}
                    seriesConfig={{
                        type: PanelSeriesType.SITE,
                        graphSeries: [
                            { label: 'Series 1 Label' },
                            { label: 'Series 2 Label' },
                            { label: 'Series 3 Label' },
                            { label: 'Series 4 Label' },
                        ],
                    }}
                    graphType="SCATTERPLOTLINE"
                    showLegend
                    disablePanelSetup
                    theme={theme}
                    xAxisTitle="Months"
                    yAxisTitle="Count"
                    panelData={{
                        lineSiteForStudy: LineSiteForStudy,
                    }}
                />

                <PanelFrame
                    title="Site Cumulative - Enrollement and Issues - Months"
                    data-testid="line-series-site-cumulative"
                    panelType={PanelType.GRAPH}
                    seriesConfig={{
                        type: PanelSeriesType.SITE_CUMULATIVE,
                        graphSeries: [
                            { label: 'Series 1 label', threshold: 3 },
                            { label: 'Series 2 label', threshold: 5 },
                            { label: 'Series 3 Label' },
                            { label: 'Series 4 Label', threshold: 15 },
                        ],
                    }}
                    graphType="SCATTERPLOTLINE"
                    showLegend
                    disablePanelSetup
                    theme={theme}
                    xAxisTitle="Months"
                    yAxisTitle="Count"
                    panelData={{
                        lineSiteForStudy: LineSiteForStudyCumulative,
                    }}
                    infoText={getLineSubtitleComponents(
                        {
                            type: PanelSeriesType.SITE_CUMULATIVE,
                            graphSeries: [
                                { label: 'Series 1 label', threshold: 3 },
                                { label: 'Series 2 label', threshold: 5 },
                                { label: 'Series 3 Label' },
                                { label: 'Series 4 Label', threshold: 15 },
                            ],
                        },
                        undefined,
                        undefined,
                        PanelAverageLineThresholdType.STUDY_MEDIAN_MEAN,
                    )}
                    subTitle={getLineSubtitle(
                        {
                            type: PanelSeriesType.SITE_CUMULATIVE,
                            graphSeries: [
                                { label: 'Series 1 label', threshold: 3 },
                                { label: 'Series 2 label', threshold: 5 },
                                { label: 'Series 3 Label' },
                                { label: 'Series 4 Label', threshold: 15 },
                            ],
                        },
                        undefined,
                        undefined,
                        PanelAverageLineThresholdType.STUDY_MEDIAN_MEAN,
                    )}
                />

                <PanelFrame
                    title="Site Average - Study Median & Mean"
                    data-testid="line-series-site-average-median"
                    panelType={PanelType.GRAPH}
                    seriesConfig={{ type: PanelSeriesType.SITE_AVERAGE }}
                    graphType="SCATTERPLOTLINE"
                    thresholdType={PanelAverageLineThresholdType.STUDY_MEDIAN_MEAN}
                    showLegend
                    disablePanelSetup
                    theme={theme}
                    xAxisTitle="Months"
                    yAxisTitle="Count"
                    panelData={{
                        threshold1: 16.0,
                        threshold2: 30.544474596713442,
                        lineAverageForStudy: LineAverageForStudy,
                    }}
                    infoText={getLineSubtitleComponents(
                        {
                            type: PanelSeriesType.SITE_AVERAGE,
                        },
                        16.0,
                        30.544474596713442,
                        PanelAverageLineThresholdType.STUDY_MEDIAN_MEAN,
                    )}
                    subTitle={getLineSubtitle(
                        {
                            type: PanelSeriesType.SITE_AVERAGE,
                        },
                        16.0,
                        30.544474596713442,
                        PanelAverageLineThresholdType.STUDY_MEDIAN_MEAN,
                    )}
                />

                <PanelFrame
                    title="Site Average - Study Specific Thresholds"
                    data-testid="line-series-site-avarage-specific"
                    panelType={PanelType.GRAPH}
                    seriesConfig={{ type: PanelSeriesType.SITE_AVERAGE }}
                    graphType="SCATTERPLOTLINE"
                    showLegend
                    disablePanelSetup
                    theme={theme}
                    panelData={{
                        threshold1: 10,
                        threshold2: 20,
                        lineAverageForStudy: LineAverageForStudySpecific,
                    }}
                    infoText={getLineSubtitleComponents(
                        {
                            type: PanelSeriesType.SITE_AVERAGE,
                        },
                        10,
                        20,
                        PanelAverageLineThresholdType.STUDY_MEDIAN_MEAN,
                    )}
                    subTitle={getLineSubtitle(
                        {
                            type: PanelSeriesType.SITE_AVERAGE,
                        },
                        10,
                        20,
                        PanelAverageLineThresholdType.STUDY_MEDIAN_MEAN,
                    )}
                />

                <PanelFrame
                    title="Event Type - Month"
                    data-testid="line-series-event"
                    panelType={PanelType.GRAPH}
                    seriesConfig={{ type: PanelSeriesType.EVENT }}
                    graphType="SCATTERPLOTLINE"
                    showLegend
                    disablePanelSetup
                    theme={theme}
                    xAxisTitle="Months"
                    yAxisTitle="Count"
                    panelData={{
                        lineEventForStudy: LineEventForStudy,
                    }}
                />

                <PanelFrame
                    title="Subject - Standard"
                    data-testid="line-series-event"
                    panelType={PanelType.GRAPH}
                    seriesConfig={{ type: PanelSeriesType.SUBJECT }}
                    graphType="SCATTERPLOTLINE"
                    showLegend
                    disablePanelSetup
                    theme={theme}
                    xAxisTitle="Months"
                    yAxisTitle="Count"
                    panelData={{
                        lineSubjectForSubjects: LineSubjectForSubject,
                    }}
                />

                <PanelFrame
                    title="Spark Line Example"
                    data-testid="spark-line-series-site-standard"
                    panelType={PanelType.GRAPH}
                    seriesConfig={{
                        type: PanelSeriesType.SITE,
                        graphSeries: [{ label: 'Series 1 Label' }],
                    }}
                    graphType="SPARKLINE"
                    showLegend={false}
                    disablePanelSetup
                    theme={theme}
                    xAxisTitle=""
                    yAxisTitle=""
                    showxAxisLabels={false}
                    panelData={{
                        categories: ['Category 1'],
                        sparkLineData: SparkLineExample,
                    }}
                    customOptions={{
                        plotOptions: {
                            series: {
                                color: styles['primary-line-color'],
                                states: {
                                    hover: {
                                        lineWidthPlus: 0,
                                    },
                                },
                            },
                        },
                        tooltip: {
                            formatter() {
                                const point = this as Point;
                                const customProps = point.options as LineSeriesDataProperties;

                                return ReactDOMServer.renderToStaticMarkup(
                                    <div>
                                        <br />
                                        <b>Score: </b>
                                        {FormatNumberForChart(customProps.score)}
                                        <br />
                                        <b>Color:</b> {RAGLabel(customProps.colorIndex as number)}
                                    </div>,
                                );
                            },
                        },
                    }}
                />
            </div>
        </>
    );
};
